import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, IconButton, Snackbar, Typography } from "@mui/material";
import FixedSelectInput from "../input/fixed-select-input";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import { useWebSocket } from "../ws/websocket";
import { replyAutomationDialog } from "../automation/automation-service";
import { useEventEmitter } from "../ws/event-context";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import NotificationDialogList from "./notification-dialog-list";
import { useRecoilState } from "recoil";
import { platformState, userInfoState } from "../global-state";
import useApiCall from "../common/api-call";
import { customFormat } from "../common/custom-format";
import { v4 as uuidv4 } from "uuid";
import { getHubStatus } from "../menu/hub-service";

dayjs.extend(relativeTime);
dayjs.extend(calendar);

const NOTIFICATION_SETTINGS_KEY = "notification_settings";

const getNotificationSettings = () => {
  const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
  return saved
    ? JSON.parse(saved)
    : {
        desktopNotificationEnabled: false,
        notificationPermission: "default",
        systemNotificationsEnabled: false,
      };
};

const getSelector = (dialog) => {
  if (dialog.select) {
    return "FIXED_SELECT";
  } else {
    throw new Error("unsupported");
  }
};

const getNeedConfirm = (dialog) => {
  if (dialog.select) {
    return false;
  } else {
    return true;
  }
};

const getValueSpec = (dialog) => {
  const spec = {};
  if (dialog.select) {
    spec.enums = dialog.select.options;
  }
  return spec;
};

const updateDialogValue = (dialog, value) => {
  if (dialog.select) {
    dialog.select.value = value;
  }
};

const notificationPredicate = (a, b) => a && b && a.id === b.id;

const dialogPredicate = (a, b) => a && b && a.dialogId === b.dialogId;

const DataSelector = ({ dialog, onConfirm, onIgnore }) => {
  const selectMap = {
    FIXED_SELECT: (f) => (
      <FixedSelectInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, value)}
        onError={(error) => handleErrorChange(error)}
        useBiToggle
      ></FixedSelectInput>
    ),
  };

  const [selector, setSelector] = useState(null);
  const [needConfirm, setNeedConfirm] = useState(false);
  const [valueSpec, setValueSpec] = useState(null);
  const [value, setValue] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelector(getSelector(dialog));
    setNeedConfirm(getNeedConfirm(dialog));
    setValueSpec(getValueSpec(dialog));
  }, []);

  const handleValueChange = async (vSpec, newValue) => {
    setValue(newValue);
    vSpec.value = newValue;
    if (!needConfirm) {
      await onConfirm(dialog, newValue);
    }
  };

  const handleErrorChange = (err) => {
    setError(err);
  };

  const handleConfirm = async () => {
    if (error) {
      return;
    }
    await onConfirm(dialog, value);
  };

  const handleIgnore = () => {
    onIgnore(dialog);
  };

  return (
    <Box>
      <Grid container spacing={2} direction="column">
        {selectMap[selector] !== undefined && (
          <Grid item xs={1}>
            <Grid container columns={{ xs: 12 }}>
              <Grid item xs>
                {selectMap[selector](valueSpec)}
              </Grid>
            </Grid>
          </Grid>
        )}
        {needConfirm && (
          <Grid item xs={1}>
            <Grid
              container
              columns={{ xs: 12 }}
              rowSpacing={2}
              justifyContent="space-between"
            >
              <Grid item xs={6}>
                <Button
                  size="medium"
                  variant="outlined"
                  fullWidth
                  onClick={handleIgnore}
                >
                  Ignore
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  size="medium"
                  variant="contained"
                  fullWidth
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const MAX_NOTIFICATIONS = 100;

const handleStorageNotifications = (notifications) => {
  const sortedNotifications = notifications.sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  const limitedNotifications = sortedNotifications.slice(0, MAX_NOTIFICATIONS);

  localStorage.setItem("notifications", JSON.stringify(limitedNotifications));

  return limitedNotifications;
};

const NotificationDialogSystem = ({
  listOpen,
  setListOpen,
  setNotificationStatus,
}) => {
  const [dialogs, setDialogs] = useState(() => {
    const saved = localStorage.getItem("dialogs");
    return saved ? JSON.parse(saved) : [];
  });
  const [dialog, setDialog] = useState(null);
  const [notification, setNotification] = useState(null);
  const [notifications, setNotifications] = useState(() => {
    const savedNotifications = localStorage.getItem("notifications");
    const parsedNotifications = savedNotifications
      ? JSON.parse(savedNotifications)
      : [];
    return handleStorageNotifications(parsedNotifications);
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [platform] = useRecoilState(platformState);
  const { apiCall } = useApiCall();
  const { subscribe } = useEventEmitter();
  const [currentTime, setCurrentTime] = useState(dayjs());
  const theme = useTheme();
  const listenerRef = useRef(null);
  const lastHealthyTimeRef = useRef(0);
  const lastHubStatusTimeRef = useRef(0);
  const hubStatusTimerRef = useRef(null);

  const [notificationSettings, setNotificationSettings] = useState(
    getNotificationSettings
  );

  const sendDesktopNotification = (notification, onClick = null) => {
    if (!notificationSettings.desktopNotificationEnabled) return;
    try {
      window.electron.ipcRenderer.send("send-notification", {
        title: notification.title,
        body: notification.message || null,
      });

      // Set up one-time click listener for this notification
      if (onClick) {
        const clickHandler = () => {
          console.log("clickHandler");
          onClick();
          window.electron.ipcRenderer.removeListener(
            "notification-clicked",
            clickHandler
          );
        };
        window.electron.ipcRenderer.on("notification-clicked", clickHandler);
      }
    } catch (error) {
      console.error("Error creating notification:", error);
    }
  };

  const checkIsHub = async () => {
    if (window.electron) {
      const hubInfo = await window.electron.ipcRenderer.invoke(
        "hub:is-hub",
        userInfo.activeScope.id
      );
      return hubInfo;
    }
    return false;
  };

  const handleSubscription = (target, data) => {
    if (target === "/dialog") {
      const payload = JSON.parse(data.payload);
      receiveDialog(payload);
    } else if (target === "/notification") {
      const payload = JSON.parse(data.payload);
      receiveNotification(payload);
    } else if (target === "/hub/status") {
      const payload = JSON.parse(data.payload);
      lastHubStatusTimeRef.current = Date.now();
      let notification = null;
      if (!payload.healthy) {
        notification = {
          id: "hub-unhealthy",
          title: "Hub Unhealthy",
          message: "Hub connection lost.",
          timestamp: new Date().toISOString(),
          isSystem: true,
          systemLevel: 3,
          isPinned: true,
        };
      } else if (!payload.channelHealthy) {
        notification = {
          id: "hub-channel-unhealthy",
          title: "Hub Unhealthy",
          message: "Some integration connections on the Hub are lost.",
          timestamp: new Date().toISOString(),
          isSystem: true,
          systemLevel: 2,
          isPinned: true,
        };
      } else {
        // Update last healthy time when we receive a healthy status
        lastHealthyTimeRef.current = Date.now();
        notification = {
          id: "hub-healthy",
          title: "Hub Healthy",
          message: "Hub connected.",
          timestamp: new Date().toISOString(),
          isSystem: true,
          systemLevel: 1,
        };
      }
      receiveHubStatus(notification);
    }
  };

  const checkHubHealth = () => {
    const now = Date.now();
    if (now - lastHubStatusTimeRef.current > 60000) {
      clearSystemNotifications([
        "hub-connecting",
        "hub-unhealthy",
        "hub-channel-unhealthy",
        "hub-healthy",
      ]);
      return;
    }
    const timeSinceLastHealthy = now - lastHealthyTimeRef.current;
    if (timeSinceLastHealthy > 60000) {
      const unhealthyNotification = {
        id: "hub-unhealthy",
        title: "Hub Unhealthy",
        message: "Hub connection lost.",
        timestamp: new Date().toISOString(),
        isSystem: true,
        systemLevel: 3,
        isPinned: true,
      };
      receiveHubStatus(unhealthyNotification);
    }
  };

  const checkInitialHubStatus = async () => {
    if (platform.source === "desktop") {
      // const hubInfo = await window.electron.ipcRenderer.invoke(
      //   "hub:get-info"
      // );
      // // TODO: use another way
      // if (hubInfo[userInfo.activeScope.id]) {
      //   const connectingNotification = {
      //     id: "hub-connecting",
      //     title: "Hub Starting",
      //     message: "This usually takes a few seconds.",
      //     timestamp: new Date().toISOString(),
      //     isSystem: true,
      //     systemLevel: 2,
      //     isPinned: true,
      //   };
      //   receiveHubStatus(connectingNotification);
      //   return;
      // }
    }
    try {
      const status = await getHubStatus(apiCall, userInfo.activeScope.id);
      if (status.error === "NOT_FOUND") {
        clearSystemNotifications([
          "hub-connecting",
          "hub-unhealthy",
          "hub-channel-unhealthy",
          "hub-healthy",
        ]);
        lastHubStatusTimeRef.current = 0;
      } else if (status?.healthy) {
        lastHealthyTimeRef.current = Date.now();
        lastHubStatusTimeRef.current = Date.now();
      } else {
        lastHubStatusTimeRef.current = 0;
        const unhealthyNotification = {
          id: "hub-unhealthy",
          title: "Hub Unhealthy",
          message: "Hub is not connected.",
          timestamp: new Date().toISOString(),
          isSystem: true,
          systemLevel: 3,
          isPinned: true,
        };
        receiveHubStatus(unhealthyNotification);
      }
    } catch (error) {
      console.error("Error checking initial hub status:", error);
    }
  };

  useEffect(() => {
    const { unsubscribe } = subscribe(handleSubscription);
    checkInitialHubStatus();
    const timer = setInterval(() => {
      checkHubHealth();
    }, 60000);
    hubStatusTimerRef.current = timer;

    return () => {
      console.log("teardown notification");
      if (hubStatusTimerRef.current) {
        clearInterval(hubStatusTimerRef.current);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    let status = 0;
    if (dialogs.length > 0) {
      status = status | 4;
    }
    if (notifications.length > 0) {
      if (notifications.some((n) => n.isSystem)) {
        status = status | 1;
      }
      if (notifications.some((n) => !n.isSystem)) {
        status = status | 2;
      }
    }
    setNotificationStatus(status);

    if (!notifications.some((n) => notificationPredicate(n, notification))) {
      setNotification(null);
    }

    if (!dialogs.some((n) => dialogPredicate(n, dialog))) {
      hideDialog();
    }

    return () => {};
  }, [dialogs, notifications]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (window.electron && userInfo?.activeScope?.id && !listenerRef.current) {
      const handleHubStatus = (data) => {
        if (data.scopeId === userInfo?.activeScope?.id) {
          let notification = null;
          if (data.connecting) {
            notification = {
              id: "hub-connecting",
              title: "Hub Starting",
              message: "This usually takes a few seconds.",
              timestamp: new Date().toISOString(),
              isSystem: true,
              systemLevel: 2,
              isPinned: true,
            };
          } else if (!data.healthy) {
            notification = {
              id: "hub-unhealthy",
              title: "Hub Unhealthy",
              message: "Hub connection lost.",
              timestamp: new Date().toISOString(),
              isSystem: true,
              systemLevel: 3,
              isPinned: true,
            };
          } else if (data.idleChannels.length > 0) {
            notification = {
              id: "hub-channel-unhealthy",
              title: "Hub Unhealthy",
              message: "Some integration connections on the Hub are lost.",
              timestamp: new Date().toISOString(),
              isSystem: true,
              systemLevel: 2,
              isPinned: true,
            };
          } else {
            lastHealthyTimeRef.current = Date.now();
            notification = {
              id: "hub-healthy",
              title: "Hub Healthy",
              message: "Hub connected.",
              timestamp: new Date().toISOString(),
              isSystem: true,
              systemLevel: 1,
            };
          }
          receiveHubStatus(notification);
        }
      };

      // Register the event listener
      window.electron.ipcRenderer.on("hub:status", handleHubStatus);
      listenerRef.current = handleHubStatus;

      return () => {
        if (listenerRef.current) {
          window.electron.ipcRenderer.removeListener(
            "hub:status",
            handleHubStatus
          );
          listenerRef.current = null;
        }
      };
    }
  }, [userInfo?.activeScope?.id]);

  const receiveHubStatus = (hubStatusNotification) => {
    if (hubStatusNotification.id !== "hub-healthy") {
      setNotifications((prevNotifications) => {
        const unhealthyNotifications = prevNotifications.filter(
          (n) =>
            (n.id === "hub-connecting" ||
              n.id === "hub-unhealthy" ||
              n.id === "hub-channel-unhealthy") &&
            n.isSystem
        );

        if (unhealthyNotifications.length > 0) {
          console.log(unhealthyNotifications, hubStatusNotification.id);
          if (unhealthyNotifications[0].id !== hubStatusNotification.id) {
            sendDesktopNotification(hubStatusNotification);
          }
        } else {
          sendDesktopNotification(hubStatusNotification);
        }

        const filteredNotifications = prevNotifications.filter(
          (n) =>
            !(
              n.id === "hub-connecting" ||
              n.id === "hub-unhealthy" ||
              n.id === "hub-channel-unhealthy"
            )
        );
        const newNotifications = [
          hubStatusNotification,
          ...filteredNotifications,
        ];
        return handleStorageNotifications(newNotifications);
      });
    } else {
      setNotifications((prevNotifications) => {
        const unhealthyNotifications = prevNotifications.filter(
          (n) =>
            (n.id === "hub-connecting" ||
              n.id === "hub-unhealthy" ||
              n.id === "hub-channel-unhealthy") &&
            n.isSystem
        );
        if (unhealthyNotifications.length > 0) {
          const newNotifications = [
            ...prevNotifications.filter(
              (n) =>
                (n.id !== "hub-connecting" &&
                  n.id !== "hub-unhealthy" &&
                  n.id !== "hub-channel-unhealthy") ||
                !n.isSystem
            ),
          ];
          sendDesktopNotification(hubStatusNotification);
          return handleStorageNotifications(newNotifications);
        }

        return prevNotifications;
      });
    }
  };

  const clearSystemNotifications = (ids) => {
    setNotifications((prevNotifications) => {
      return prevNotifications.filter(
        (n) => !ids.includes(n.id) || !n.isSystem
      );
    });
  };

  const receiveNotification = (payload) => {
    console.log("receiveNotification", payload);
    setNotifications((prevNotifications) => {
      if (!payload.id) {
        payload.id = uuidv4();
      }
      payload.fromEvent = true;
      const predicate = (item) => notificationPredicate(item, payload);
      let newNotifications = [...prevNotifications];
      if (!newNotifications.some(predicate)) {
        newNotifications.push(payload);
        newNotifications = handleStorageNotifications(newNotifications);
        setNotification(payload);
        sendDesktopNotification(payload, () =>
          handleNotificationClick(payload)
        );
        setDialog(null);
      }
      return newNotifications;
    });
  };

  const receiveDialog = (dialogChanges) => {
    console.log(dialogChanges);
    let newd = [...dialogs];
    console.log(newd);

    for (const d of dialogChanges.changes) {
      d.dialog.title = "Action Required";
      if (d.type === "DELETE_DIALOG") {
        newd = newd.filter(
          (d) =>
            d.scopeId !== dialogChanges.scopeId ||
            d.dialogId !== d.deleteDialogId
        );
      } else if (d.type === "UPSERT") {
        const idx = newd.findIndex(
          (dd) =>
            dd.scopeId === dialogChanges.scopeId &&
            dd.automationId === d.dialog.automationId &&
            dd.dialogId === d.dialog.dialogId
        );
        if (idx !== -1) {
          newd[idx] = d.dialog;
        } else {
          newd.push(d.dialog);
          sendDesktopNotification(d.dialog, () => showDialog(d.dialog));
          showDialog(d.dialog);
          setNotification(null);
        }
      }
    }
    setDialogs(newd);
    localStorage.setItem("dialogs", JSON.stringify(newd));
  };

  const clearAllNotifications = () => {
    setNotifications((prevNotifications) => {
      // Keep system notifications when clearing all
      const systemNotifications = prevNotifications.filter((n) => n.isSystem);
      localStorage.setItem(
        "notifications",
        JSON.stringify(systemNotifications)
      );
      return systemNotifications;
    });
    setNotification(null);
  };

  const handleNotificationClick = useCallback(
    (notification) => {
      console.log("handleNotificationClick", notification);
      const notificationToShow = { ...notification, fromEvent: false };
      setNotification(notificationToShow);
    },
    [platform]
  );

  const handleDialogTimeout = (dialog) => {
    setDialogs((prev) => {
      const predicate = (item) => item.dialogId === dialog.dialogId;
      const newd = prev.filter((item) => !predicate(item));
      localStorage.setItem("dialogs", JSON.stringify(newd));
      return newd;
    });
  };

  const handleDialogClick = useCallback((dialog) => {
    showDialog(dialog);
  }, []);

  const showDialog = (dialog) => {
    setDialog(dialog);
    setDrawerOpen(true);
  };

  const hideDialog = () => {
    setDrawerOpen(false);
    setDialog(null);
  };

  const handleDialogConfirm = async (dialog, value) => {
    updateDialogValue(dialog, value);
    await replyAutomationDialog(apiCall, userInfo.activeScope.id, dialog);
    setDrawerOpen(false);
    setDialog(null);

    setDialogs((prevDialogs) => {
      const predicate = (item) => dialogPredicate(item, dialog);
      const newDialogs = prevDialogs.filter((item) => !predicate(item));
      localStorage.setItem("dialogs", JSON.stringify(newDialogs));
      return newDialogs;
    });
  };

  const handleDeleteNotification = (item) => {
    if (item.isPinned) {
      setNotification(null);
      return;
    }

    if (item.dialogId) {
      // Handle dialog deletion
      setDialogs((prevDialogs) => {
        const predicate = (d) => d.dialogId === item.dialogId;
        const newDialogs = prevDialogs.filter((d) => !predicate(d));
        localStorage.setItem("dialogs", JSON.stringify(newDialogs));
        return newDialogs;
      });
    } else {
      // Handle notification deletion
      setNotifications((prevNotifications) => {
        const predicate = (n) => notificationPredicate(n, item);
        const newNotifications = prevNotifications.filter((n) => !predicate(n));
        localStorage.setItem("notifications", JSON.stringify(newNotifications));
        return newNotifications;
      });

      // If the deleted notification is currently being displayed, clear it
      if (notification && notificationPredicate(notification, item)) {
        setNotification(null);
      }
    }
  };

  return (
    <div>
      <Box role="presentation">
        {dialog && (
          <Snackbar
            open={drawerOpen}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={null}
            onClose={hideDialog}
            message={
              <Box
                sx={{
                  width: "60vw",
                  maxWidth: "80vw",
                  "@media (max-width: 768px)": {
                    width: "80vw",
                  },
                }}
              >
                <Box flexGrow={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      marginBottom: 2,
                      display: "block",
                    }}
                  >
                    {customFormat(dialog.timestamp, currentTime)}
                  </Typography>
                  <Typography variant="h6">{dialog.message}</Typography>
                  <Box role="presentation" p={2}>
                    <DataSelector
                      dialog={dialog}
                      onConfirm={handleDialogConfirm}
                      onIgnore={hideDialog}
                    ></DataSelector>
                  </Box>
                </Box>
              </Box>
            }
            ContentProps={{
              sx: {
                backgroundColor: "background.card",
                color: "text.primary",
                p: 2,
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              },
            }}
          />
        )}
      </Box>

      {notification && (
        <Snackbar
          open={true}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={notification.fromEvent ? 10000 : null}
          onClose={() => {
            setNotification(null);
          }}
          message={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                width: "60vw",
                maxWidth: "80vw",
                "@media (max-width: 768px)": {
                  width: "80vw",
                },
              }}
            >
              <Box flexGrow={1}>
                <Typography
                  variant="caption"
                  sx={{
                    marginBottom: 2,
                    display: "block",
                  }}
                >
                  {customFormat(notification.timestamp, currentTime)}
                </Typography>
                <Typography variant="h6">{notification.title}</Typography>
                <Typography>{notification.message}</Typography>
              </Box>
              <Box>
                <Box>
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => handleDeleteNotification(notification)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          }
          ContentProps={{
            sx: {
              backgroundColor: "background.card",
              color: "text.primary",
              p: 2,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            },
          }}
        />
      )}
      <NotificationDialogList
        notifications={notifications}
        setNotification={setNotification}
        dialogs={dialogs}
        onClose={() => setListOpen(false)}
        onNotificationClick={handleNotificationClick}
        onDialogTimeout={handleDialogTimeout}
        onDialogClick={handleDialogClick}
        onClearNotifications={clearAllNotifications}
        open={listOpen}
        onDelete={handleDeleteNotification}
      />
    </div>
  );
};

export default NotificationDialogSystem;
